import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { usePageTitle } from '@/hooks/usePageTitle';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Workflow,
  Plus,
  LayoutGrid,
  List,
  Copy,
  Trash2,
  Clock,
  ChevronLeft,
  Loader2,
  User,
  CheckCircle2,
  AlertCircle,
  Building2,
  Tag,
  FolderTree,
  Upload,
  FileImage,
  Settings2,
  X,
  BookTemplate,
  Blocks,
  ArrowRight,
} from 'lucide-react';
import { FlowRowActions } from '@/components/eduflow/FlowRowActions';
import { ContentDashboardActionButton } from '@/components/dashboard/ContentDashboardActionButton';
import { formateurFormationDashboardPath } from '@/lib/content-dashboard-paths';
import { buildEduFlowEditUrl } from '@/lib/eduflow-designer-return';
import { getEduFlowEditPath } from '@/api/eduflow-simple.api';
import { LoFormationTrainerRowCard } from '@/components/lyrraone/formation-cards';
import { resolveFormationCoverUrl } from '@/lib/eduflow-formation-cover';
import { cn, formatRelativeDate } from '@/lib/utils';
import { rtlMirrorHorizontalChevron } from '@/lib/locale-direction';
import { api } from '@/lib/api';
import { ensureSellerBillingForPublish, notifyEduFlowPublishError } from '@/lib/lyrraone-publish-guard';
import { extractInstitutionCategoriesFromApiResponse, institutionCategoriesApiPath } from '@/lib/institution-categories-from-api';
import {
  localizedInstitutionCategoryDescription,
  localizedInstitutionCategoryName,
} from '@/lib/institution-category-i18n';
import { imagesApi } from '@/api/images.api';
import { eduflowApi } from '@/api/eduflow.api';
import { toast } from '@/components/ui/Toast';
import { Button } from '@/components/ui/Button';
import { Tooltip } from '@/components/ui/Tooltip';
import { OptimizedImage } from '@/components/ui/OptimizedImage';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/ui/Modal';
import { Switch } from '@/components/ui/Switch';
import { Input } from '@/components/ui/Input';
import { useAuthStore } from '@/stores/auth.store';
import { isPlatformEditorRole } from '@/lib/editor-role';
import { listTrainerEditorSharedEduflows } from '@/api/editor.api';
import { PublishToStorePanel } from '@/components/store/PublishToStorePanel';
import { FunnelAiWizardModal } from '@/components/funnel/FunnelAiWizardModal';
import { fetchAvailableFormations } from '@/lib/sales-funnel';
import { isLyrraOne } from '@/lib/platform';
import { useLyrraOnePlan } from '@/hooks/useLyrraOnePlan';
import { LyrraOnePlanQuotaBanner } from '@/components/lyrraone/LyrraOnePlanQuotaBanner';
import { TrainerRightPanelHeader } from '@/components/dashboard/TrainerRightPanelHeader';
import { InstitutionStyledSearchInput } from '@/components/institution/InstitutionStyledSearchInput';
import { getCookie, setCookie } from '@/lib/cookies';

/** Même système que la page Vidéos (panneaux formateur) */
const btnPrimaryInst = cn(
  '!bg-[color:var(--inst-player-primary,#0C1C38)] hover:!bg-[color:var(--inst-player-primary-hover,#19335E)] !text-white',
  'focus-visible:!ring-[color:var(--inst-player-primary,#0C1C38)]',
);
const panelShellClass =
  'fixed end-0 top-0 z-50 flex h-[100dvh] w-full max-w-md flex-col border-s border-[color:var(--inst-player-border,#e5e7eb)] bg-[color:var(--inst-player-surface,#ffffff)] shadow-2xl dark:border-gray-800 dark:bg-gray-900';
const panelShellWideClass =
  'fixed end-0 top-0 z-50 flex h-[100dvh] w-full max-w-xl flex-col border-s border-[color:var(--inst-player-border,#e5e7eb)] bg-[color:var(--inst-player-surface,#ffffff)] shadow-2xl dark:border-gray-800 dark:bg-gray-900';
const panelBackdropClass = 'fixed inset-0 z-40 bg-black/30 backdrop-blur-sm';
const panelInputClass =
  'w-full rounded-xl border border-[color:var(--inst-player-border,#e5e7eb)] bg-[color:var(--inst-player-surface,#ffffff)] px-3 py-2 text-sm text-[color:var(--inst-player-text,#111827)] placeholder:text-[color:var(--inst-player-text-muted,#6b7280)] focus:border-[color:var(--inst-player-primary,#0C1C38)] focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_25%,transparent)] dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100';
const panelLabelClass =
  'mb-1.5 block text-sm font-medium text-[color:var(--inst-player-text,#111827)] dark:text-gray-200';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface FlowListItem {
  id: string;
  title: string;
  description?: string;
  /** Couverture (/uploads/… ou URL absolue) — champ EduFlow ou sync catalogue. */
  coverImageUrl?: string | null;
  userId?: string;
  status: 'draft' | 'published' | 'archived';
  creationMode?: 'advanced' | 'simple';
  blocks: unknown[];
  estimatedDuration?: number;
  updatedAt: string;
  createdAt?: string;
  /** Issu d’une fiche catalogue / besoin RH */
  fromInstitutionTrainingNeed?: boolean;
  listKind?: 'eduflow' | 'institution_shell' | 'editor_shared';
  /** Maison d’édition (onglet formations éditeur). */
  editorName?: string;
  shareId?: string;
  shortCodePublic?: string;
  categories?: Array<{
    id: string;
    name: string;
    color?: string | null;
    description?: string | null;
    coverImageUrl?: string | null;
    nameTranslations?: unknown;
    descriptionTranslations?: unknown;
  }>;
}

interface FlowCategoryOption {
  id: string;
  name: string;
  color: string;
  description?: string;
  coverImageUrl?: string | null;
  nameTranslations?: unknown;
  descriptionTranslations?: unknown;
}

/** Vue catégories : entrée « Sans catégorie » */
const CATEGORY_BROWSE_UNCATEGORIZED = '__uncategorized__';

/** Préférences d’affichage liste formations (dashboard formateur), persistées en cookie. */
const EDUFLOW_LIST_VIEW_MODE_COOKIE = 'lyrra_eduflow_list_view_mode';
const EDUFLOW_LIST_CATEGORY_BROWSE_COOKIE = 'lyrra_eduflow_list_category_browse';

type EduflowListViewMode = 'grid' | 'list' | 'categories';

function readEduflowListViewModeFromCookie(): EduflowListViewMode {
  if (typeof document === 'undefined') return 'list';
  const raw = getCookie(EDUFLOW_LIST_VIEW_MODE_COOKIE);
  if (raw === 'grid' || raw === 'list' || raw === 'categories') return raw;
  return 'list';
}

const CATEGORY_BROWSE_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function readEduflowListCategoryBrowseFromCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const raw = getCookie(EDUFLOW_LIST_CATEGORY_BROWSE_COOKIE);
  if (raw == null || raw === '') return null;
  if (raw === CATEGORY_BROWSE_UNCATEGORIZED) return raw;
  const t = raw.trim();
  if (CATEGORY_BROWSE_UUID_RE.test(t)) return t;
  return null;
}

interface CategoryGroupDisplay {
  id: string;
  name: string;
  color: string;
  description: string;
  coverImageUrl: string | null;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const STATUS_STYLE: Record<string, { color: string; dot: string }> = {
  draft: {
    color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    dot: 'bg-slate-400',
  },
  published: {
    color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    dot: 'bg-emerald-500',
  },
  archived: {
    color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    dot: 'bg-amber-500',
  },
};

/* ------------------------------------------------------------------ */
/*  Helper components                                                  */
/* ------------------------------------------------------------------ */


function FlowCategoriesPanel({
  flow,
  options,
  selectedIds,
  onToggle,
  onClose,
  onSave,
  saving,
}: {
  flow: FlowListItem;
  options: FlowCategoryOption[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  onClose: () => void;
  onSave: () => void;
  saving: boolean;
}) {
  const { t, i18n } = useTranslation();
  if (typeof document === 'undefined') return null;
  return createPortal(
    <>
      <div className={panelBackdropClass} onClick={onClose} aria-hidden />
      <div className={panelShellClass} role="dialog" aria-modal="true" aria-labelledby="flow-categories-title">
        <TrainerRightPanelHeader
          icon={Tag}
          title={t('eduflow.list.categoriesPanelTitle', 'Catégories')}
          titleId="flow-categories-title"
          description={flow.title}
          onClose={onClose}
          closeDisabled={saving}
          closeAriaLabel={t('btn.close')}
        />
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-6">
            {options.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-gray-400">
                {t('eduflow.list.noCategoryAvailable', 'Aucune catégorie disponible.')}
              </p>
            ) : (
              options.map((cat) => {
                const checked = selectedIds.includes(cat.id);
                return (
                  <div key={cat.id} className="flex cursor-pointer items-center gap-3 rounded-lg border border-slate-200 dark:border-gray-600 p-3 hover:bg-slate-50 dark:hover:bg-gray-800">
                    <Switch
                      size="sm"
                      checked={checked}
                      onChange={(next) => {
                        if (next !== checked) onToggle(cat.id);
                      }}
                    />
                    <span className="h-3 w-3 rounded-full" style={{ backgroundColor: cat.color }} aria-hidden />
                    <span className="text-sm font-medium text-slate-800 dark:text-gray-200">
                      {localizedInstitutionCategoryName(cat, i18n.language)}
                    </span>
                  </div>
                );
              })
            )}
          </div>
          <div className="flex justify-end gap-3 border-t border-slate-200 dark:border-gray-700 p-4">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              {t('btn.cancel')}
            </Button>
            <Button variant="primary" className={btnPrimaryInst} onClick={onSave} loading={saving}>
              {t('btn.save')}
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

function duplicateDefaultTitle(flow: FlowListItem, suffix: string): string {
  const s = suffix.trim();
  return s ? `${flow.title} (${s})` : `${flow.title} (copie)`;
}

function FlowPropertiesPanel({
  flow,
  mode = 'properties',
  onClose,
  onSaved,
  onDuplicated,
}: {
  flow: FlowListItem;
  mode?: 'properties' | 'duplicate';
  onClose: () => void;
  onSaved: (flowId: string, patch: Partial<FlowListItem>) => void;
  onDuplicated?: (item: FlowListItem) => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(flow.title);
  const [description, setDescription] = useState(flow.description ?? '');
  const [coverUrl, setCoverUrl] = useState<string | null>(flow.coverImageUrl ?? null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const isDuplicate = mode === 'duplicate';

  useEffect(() => {
    if (mode === 'duplicate') {
      setTitle(duplicateDefaultTitle(flow, t('eduflow.list.duplicateTitleSuffix', 'copie')));
      setDescription(flow.description ?? '');
      setCoverUrl(flow.coverImageUrl ?? null);
    } else {
      setTitle(flow.title);
      setDescription(flow.description ?? '');
      setCoverUrl(flow.coverImageUrl ?? null);
    }
  }, [flow.id, flow.title, flow.description, flow.coverImageUrl, mode, t]);

  const resolvedPreview = resolveFormationCoverUrl(coverUrl);

  const save = async () => {
    if (!title.trim()) {
      toast.error(t('eduflow.createFormation.titleRequired'));
      return;
    }
    setSaving(true);
    try {
      if (isDuplicate) {
        const resp = await api.post<{
          data?: { id: string; title?: string | null; updatedAt?: Date | string };
          id?: string;
        }>(`/eduflows/${flow.id}/duplicate`, {
          title: title.trim(),
          description: description.trim() === '' ? null : description.trim(),
        });
        const created =
          resp && typeof resp === 'object' && 'data' in resp && resp.data && typeof resp.data === 'object'
            ? resp.data
            : (resp as { id?: string; title?: string | null; updatedAt?: Date | string });
        const id = created?.id;
        if (!id) throw new Error('no id');
        const newItem: FlowListItem = {
          ...flow,
          id,
          title:
            (typeof created.title === 'string' && created.title.trim() ? created.title : null) ??
            title.trim(),
          description: description.trim() === '' ? undefined : description.trim(),
          status: 'draft',
          updatedAt:
            typeof created.updatedAt === 'string'
              ? created.updatedAt
              : created.updatedAt instanceof Date
                ? created.updatedAt.toISOString()
                : new Date().toISOString(),
          listKind: 'eduflow',
        };
        onDuplicated?.(newItem);
        toast.success(t('eduflow.list.duplicateSuccess', 'Formation dupliquée.'));
        onClose();
      } else {
        await api.put(`/eduflows/${flow.id}`, {
          title: title.trim(),
          description: description.trim() === '' ? null : description.trim(),
          coverImageUrl: coverUrl,
        });
        onSaved(flow.id, {
          title: title.trim(),
          description: description.trim() === '' ? undefined : description.trim(),
          coverImageUrl: coverUrl,
          updatedAt: new Date().toISOString(),
        });
        toast.success(t('eduflow.list.propertiesSaved', 'Propriétés enregistrées.'));
        onClose();
      }
    } catch {
      toast.error(
        isDuplicate
          ? t('eduflow.duplicateFlowError', 'Impossible de dupliquer la formation.')
          : t('eduflow.list.propertiesSaveError', 'Impossible d’enregistrer les propriétés.'),
      );
    } finally {
      setSaving(false);
    }
  };

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/')) return;
    setUploading(true);
    try {
      const res = await imagesApi.upload(file, 'eduflow');
      const row = res && typeof res === 'object' && 'data' in res ? (res as { data?: { url?: string } }).data : null;
      const url = typeof row?.url === 'string' ? row.url : null;
      if (url) setCoverUrl(url);
    } catch {
      toast.error(t('eduflow.list.propertiesUploadError', 'Échec du téléversement de l’image.'));
    } finally {
      setUploading(false);
    }
  };

  if (typeof document === 'undefined') return null;
  return createPortal(
    <>
      <div className={panelBackdropClass} onClick={() => !saving && !uploading && onClose()} aria-hidden />
      <div className={panelShellWideClass} role="dialog" aria-modal="true" aria-labelledby="flow-props-panel-title">
        <TrainerRightPanelHeader
          icon={isDuplicate ? Copy : Settings2}
          title={
            isDuplicate
              ? t('eduflow.list.duplicatePanelTitle', 'Dupliquer la formation')
              : t('eduflow.list.propertiesPanelTitle', 'Propriétés de la formation')
          }
          titleId="flow-props-panel-title"
          description={
            isDuplicate
              ? t('eduflow.list.duplicatePanelSubtitle', 'Formation d’origine : {{name}}', {
                  name: flow.title,
                })
              : flow.title
          }
          onClose={() => !saving && !uploading && onClose()}
          closeDisabled={saving || uploading}
          closeAriaLabel={t('btn.close')}
          supportFabPanelMaxWidth="xl"
        />
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-6">
            <div>
              <label htmlFor="flow-props-title" className={panelLabelClass}>
                {t('eduflow.createFormation.titleLabel')}
              </label>
              <input
                id="flow-props-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={panelInputClass}
                disabled={saving}
              />
            </div>
            <div>
              <label htmlFor="flow-props-desc" className={panelLabelClass}>
                {t('eduflow.createFormation.descriptionLabel')}
              </label>
              <textarea
                id="flow-props-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className={cn(panelInputClass, 'resize-y')}
                disabled={saving}
              />
            </div>
            {isDuplicate ? (
              <p className="text-sm text-[color:var(--inst-player-text-muted,#6b7280)] dark:text-gray-400">
                {t(
                  'eduflow.list.duplicateCoverHint',
                  'La couverture et le contenu pédagogique de la formation d’origine seront repris.',
                )}
              </p>
            ) : (
              <div>
                <span className={panelLabelClass}>{t('eduflow.list.coverLabel', 'Image de couverture')}</span>
                <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-start">
                  <div className="relative h-36 w-full overflow-hidden rounded-xl border border-[color:var(--inst-player-border,#e5e7eb)] bg-slate-100 dark:border-gray-600 dark:bg-gray-800/50 sm:h-40 sm:w-40 sm:shrink-0">
                    {resolvedPreview ? (
                      <OptimizedImage priority src={resolvedPreview} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-slate-400">
                        <FileImage className="h-10 w-10 opacity-40" />
                      </div>
                    )}
                    {uploading ? (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-900/80">
                        <Loader2 className="h-8 w-8 animate-spin text-[color:var(--inst-player-primary,#0C1C38)]" />
                      </div>
                    ) : null}
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickFile} />
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      disabled={saving || uploading}
                      className={cn(
                        'inline-flex items-center justify-center gap-2 rounded-xl border border-[color:var(--inst-player-border,#e5e7eb)] px-4 py-2.5 text-sm font-semibold',
                        'text-[color:var(--inst-player-text,#111827)] hover:bg-slate-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-800',
                      )}
                    >
                      <Upload className="h-4 w-4" />
                      {t('eduflow.list.coverUpload', 'Choisir une image')}
                    </button>
                    {coverUrl ? (
                      <button
                        type="button"
                        onClick={() => setCoverUrl(null)}
                        disabled={saving || uploading}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50 dark:text-red-400"
                      >
                        <X className="h-3.5 w-3.5" />
                        {t('eduflow.list.coverRemove', 'Retirer la couverture')}
                      </button>
                    ) : null}
                    <p className="text-xs text-[color:var(--inst-player-text-muted,#6b7280)] dark:text-gray-400">
                      {t(
                        'eduflow.list.coverHint',
                        'Affichée dans les listes et l’espace apprenant. Formats image courants, env. 5 Mo max.',
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3 border-t border-[color:var(--inst-player-border,#e5e7eb)] p-4 dark:border-gray-700">
            <Button variant="outline" onClick={onClose} disabled={saving || uploading}>
              {t('btn.cancel')}
            </Button>
            <Button variant="primary" className={btnPrimaryInst} onClick={() => void save()} loading={saving}>
              {isDuplicate ? t('btn.duplicate') : t('btn.save')}
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

/* ------------------------------------------------------------------ */
/*  Empty state                                                        */
/* ------------------------------------------------------------------ */

function EmptyState({
  hasFlows,
  onOpenCreatePanel,
  creatingFlow,
  emptyTitle,
  emptyHint,
}: {
  hasFlows: boolean;
  onOpenCreatePanel?: () => void;
  creatingFlow: boolean;
  emptyTitle?: string;
  emptyHint?: string;
}) {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 dark:border-gray-700 bg-slate-50/50 dark:bg-gray-900/50 px-8 py-16"
    >
      <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_10%,transparent)]">
        <Workflow className="h-10 w-10 text-[color:var(--inst-player-primary,#0C1C38)]" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
        {emptyTitle ??
          (hasFlows ? t('eduflow.list.emptyFilteredTitle') : t('eduflow.list.emptyFirstTitle'))}
      </h3>
      <p className="mt-2 max-w-sm text-center text-sm text-slate-500 dark:text-gray-400">
        {emptyHint ?? (hasFlows ? t('eduflow.list.emptyFilteredHint') : t('eduflow.list.emptyFirstHint'))}
      </p>
      {!hasFlows && onOpenCreatePanel && (
        <button
          type="button"
          onClick={onOpenCreatePanel}
          disabled={creatingFlow}
          className={cn(
            'mt-6 inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-sm',
            'bg-[color:var(--inst-player-primary,#0C1C38)] hover:bg-[color:var(--inst-player-primary-hover,#19335E)] focus:outline-none focus:ring-2 focus:ring-[color:var(--inst-player-primary,#0C1C38)] focus:ring-offset-2',
            'transition-all duration-200 disabled:opacity-60',
          )}
        >
          {creatingFlow ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
          {t('eduflow.list.emptyCreateCta')}
        </button>
      )}
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Flow card                                                          */
/* ------------------------------------------------------------------ */

/** URL affichable pour la couverture formation (uploads ou défaut LyrraOne). */
function flowCoverDisplayUrl(flow: FlowListItem): string {
  return resolveFormationCoverUrl(flow.coverImageUrl);
}

type FlowCardProps = {
  flow: FlowListItem;
  /** Langue UI i18next (`i18n.language`) pour libellés de catégories institution. */
  categoryUiLang: string;
  isCollaborative: boolean;
  collaborativeLabel: string;
  index: number;
  viewMode: 'grid' | 'list' | 'categories';
  isEditing: boolean;
  editingTitle: string;
  onEditTitle: () => void;
  onTitleChange: (v: string) => void;
  onSaveTitle: () => void;
  onCancelEdit: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onExportJson?: () => void;
  onTogglePublish: () => void;
  hidePublish?: boolean;
  onArchive: () => void;
  onRestoreArchive: () => void;
  onDelete: () => void;
  onOpenOffer?: () => void;
  onGenerateFunnelAi?: () => void;
  onManageCategories: () => void;
  onOpenProperties: () => void;
  onOpenDashboard?: () => void;
};

const FlowCard = React.forwardRef<HTMLDivElement, FlowCardProps>(({
  flow,
  categoryUiLang,
  isCollaborative,
  collaborativeLabel,
  index,
  viewMode,
  isEditing,
  editingTitle,
  onEditTitle,
  onTitleChange,
  onSaveTitle,
  onCancelEdit,
  onEdit,
  onDuplicate,
  onExportJson,
  onTogglePublish,
  hidePublish = false,
  onArchive,
  onRestoreArchive,
  onDelete,
  onOpenOffer,
  onGenerateFunnelAi,
  onManageCategories,
  onOpenProperties,
  onOpenDashboard,
}, ref) => {
  const { t } = useTranslation();
  const coverSrc = flowCoverDisplayUrl(flow);
  const statusStyle = STATUS_STYLE[flow.status] ?? STATUS_STYLE.draft;
  const statusLabel = t(`eduflowList.status.${flow.status}`);
  const isShell = flow.listKind === 'institution_shell';
  const isEditorShared = flow.listKind === 'editor_shared';
  const enterpriseLinked = Boolean(flow.fromInstitutionTrainingNeed && !isShell && !isEditorShared);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') onSaveTitle();
    if (e.key === 'Escape') onCancelEdit();
  };

  /* ---- list row ---- */
  if (viewMode === 'list') {
    const fallbackMeta = isShell
      ? t('eduflow.list.noContent', 'Sans contenu')
      : [
          t('eduflow.list.blocksCount', { count: flow.blocks.length }),
          flow.estimatedDuration != null
            ? t('eduflow.list.minutesShort', { count: flow.estimatedDuration })
            : null,
        ].filter(Boolean).join(' · ');

    return (
      <motion.div
        ref={ref}
        layout
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 10 }}
        transition={{ duration: 0.2, delay: index * 0.03 }}
      >
        <LoFormationTrainerRowCard
          title={isEditing ? editingTitle : flow.title}
          formationId={flow.id}
          coverImageUrl={flow.coverImageUrl}
          fallbackMeta={fallbackMeta}
          onClick={onEdit}
          actionsSlot={(
            <div className="flex items-center gap-1">
              {onOpenDashboard ? (
                <ContentDashboardActionButton onClick={onOpenDashboard} />
              ) : null}
              <FlowRowActions
                isShell={isShell}
                isEditorShared={isEditorShared}
                enterpriseLinked={enterpriseLinked}
                status={flow.status}
                hidePublish={hidePublish}
                onEdit={onEdit}
                onDuplicate={onDuplicate}
                onExportJson={onExportJson}
                onTogglePublish={onTogglePublish}
                onArchive={onArchive}
                onRestoreArchive={onRestoreArchive}
                onDelete={onDelete}
                onOpenOffer={onOpenOffer}
                onGenerateFunnelAi={onGenerateFunnelAi}
                onManageCategories={onManageCategories}
                onOpenProperties={onOpenProperties}
                onOpenDashboard={onOpenDashboard}
              />
            </div>
          )}
        />
        {isEditing ? (
          <input
            autoFocus
            value={editingTitle}
            onChange={(e) => onTitleChange(e.target.value)}
            onBlur={onSaveTitle}
            onKeyDown={handleKeyDown}
            className="mt-2 w-full rounded-lg border border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_35%,transparent)] bg-white px-3 py-2 text-sm font-semibold text-slate-900 dark:bg-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_18%,transparent)]"
          />
        ) : null}
      </motion.div>
    );
  }

  /* ---- grid card ---- */
  return (
    <motion.div
      ref={ref}
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.25, delay: index * 0.05 }}
      className={cn(
        'group relative flex cursor-pointer flex-col rounded-xl border border-slate-200/60 dark:border-gray-700 bg-white dark:bg-gray-900',
        'transition-all duration-200 hover:border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_30%,transparent)] hover:shadow-lg',
      )}
      onClick={onEdit}
    >
      <div className="relative aspect-[16/10] w-full overflow-hidden rounded-t-xl bg-slate-100 dark:bg-gray-800">
        <OptimizedImage
          src={coverSrc}
          alt=""
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
        />
      </div>
      {/* colour strip */}
      <div
        className={cn(
          'h-1.5',
          isShell
            ? 'bg-amber-400'
            : flow.status === 'published'
              ? 'bg-emerald-500'
              : flow.status === 'archived'
                ? 'bg-amber-400'
                : 'bg-slate-300 dark:bg-gray-600',
        )}
      />

      <div className="flex flex-1 flex-col p-4">
        {/* badge + actions */}
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {!isShell ? (
              <span
                className={cn(
                  'rounded-full px-2.5 py-0.5 text-xs font-medium',
                  statusStyle.color,
                )}
              >
                {statusLabel}
              </span>
            ) : (
              <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                {t('eduflowList.badge.enterpriseShell')}
              </span>
            )}
            {isEditorShared && (
              <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-900 dark:bg-violet-950/40 dark:text-violet-200">
                <BookOpen className="h-3 w-3" />
                {flow.editorName ?? t('eduflowList.badge.editorShared', 'Éditeur')}
              </span>
            )}
            {enterpriseLinked && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                <Building2 className="h-3 w-3" />
                {t('eduflowList.badge.enterpriseLinked')}
              </span>
            )}
            {isCollaborative && !isShell && !isEditorShared && (
              <span className="rounded-full bg-violet-100 dark:bg-violet-900/30 px-2.5 py-0.5 text-xs font-medium text-violet-700 dark:text-violet-400">
                {collaborativeLabel}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {onOpenDashboard ? (
              <ContentDashboardActionButton onClick={onOpenDashboard} />
            ) : null}
            <FlowRowActions
              isShell={isShell}
              isEditorShared={isEditorShared}
              enterpriseLinked={enterpriseLinked}
              status={flow.status}
              hidePublish={hidePublish}
              onEdit={onEdit}
              onDuplicate={onDuplicate}
              onExportJson={onExportJson}
              onTogglePublish={onTogglePublish}
              onArchive={onArchive}
              onRestoreArchive={onRestoreArchive}
              onDelete={onDelete}
              onOpenOffer={onOpenOffer}
              onGenerateFunnelAi={onGenerateFunnelAi}
              onManageCategories={onManageCategories}
              onOpenProperties={onOpenProperties}
              onOpenDashboard={onOpenDashboard}
            />
          </div>
        </div>

        {/* title */}
        <div onClick={(e) => e.stopPropagation()}>
          {isEditing && !isShell ? (
            <input
              autoFocus
              value={editingTitle}
              onChange={(e) => onTitleChange(e.target.value)}
              onBlur={onSaveTitle}
              onKeyDown={handleKeyDown}
              className="w-full rounded border border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_35%,transparent)] bg-transparent px-2 py-0.5 text-sm font-semibold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_18%,transparent)]"
            />
          ) : (
            <h3
              className={cn(
                'line-clamp-2 text-sm font-semibold text-slate-900 dark:text-white',
                !isShell && !isEditorShared && 'cursor-text',
              )}
              onDoubleClick={isShell || isEditorShared ? undefined : onEditTitle}
            >
              {flow.title}
            </h3>
          )}
        </div>

        {flow.description && (
          <p className="mt-1.5 line-clamp-2 text-xs text-slate-500 dark:text-gray-400">
            {flow.description}
          </p>
        )}
        {Array.isArray(flow.categories) && flow.categories.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {flow.categories.slice(0, 3).map((cat) => (
              <span key={cat.id} className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-gray-800 px-2 py-0.5 text-[11px] text-slate-700 dark:text-gray-300">
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: cat.color ?? '#94a3b8' }} aria-hidden />
                {localizedInstitutionCategoryName(cat, categoryUiLang)}
              </span>
            ))}
            {flow.categories.length > 3 && (
              <span className="inline-flex items-center rounded-full bg-slate-100 dark:bg-gray-800 px-2 py-0.5 text-[11px] text-slate-600 dark:text-gray-400">
                +{flow.categories.length - 3}
              </span>
            )}
          </div>
        )}
        {isShell && (
          <p className="mt-1.5 line-clamp-3 text-xs text-amber-800/90 dark:text-amber-200/90">
            {t('eduflow.list.shellHint')}
          </p>
        )}

        {/* stats */}
        <div className="mt-auto flex items-center gap-3 pt-4 text-xs text-slate-400 dark:text-gray-500">
          <span>{isShell ? t('eduflow.list.noContent', 'Sans contenu') : t('eduflow.list.blocksCount', { count: flow.blocks.length })}</span>
          {!isShell && flow.estimatedDuration != null && (
            <span>{flow.estimatedDuration} min</span>
          )}
          <span className="ms-auto flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatRelativeDate(flow.updatedAt)}
          </span>
        </div>
      </div>
    </motion.div>
  );
});
FlowCard.displayName = 'FlowCard';

/* ------------------------------------------------------------------ */
/*  Main page component                                                */
/* ------------------------------------------------------------------ */


export function EduFlowListPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { t, i18n } = useTranslation();
  usePageTitle(t('nav.eduflowList', 'EduFlows'));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const isEditorAccount = isPlatformEditorRole(user?.role);
  const { hasFeature, quota } = useLyrraOnePlan();
  const canGenerateFunnelAi = isLyrraOne() && hasFeature('expertAiAssistant');
  const [flows, setFlows] = useState<FlowListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<EduflowListViewMode>(() => readEduflowListViewModeFromCookie());
  const [activeFilter, setActiveFilter] = useState('all');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [creatingFlow, setCreatingFlow] = useState(false);
  const [createPanelOpen, setCreatePanelOpen] = useState(false);
  const [newFormationTitle, setNewFormationTitle] = useState('');
  const [newFormationDescription, setNewFormationDescription] = useState('');
  const [createMode, setCreateMode] = useState<'simple' | 'advanced'>('simple');
  const [offerFlow, setOfferFlow] = useState<FlowListItem | null>(null);
  const [funnelAiFlow, setFunnelAiFlow] = useState<FlowListItem | null>(null);
  const [flowIdsWithoutFunnel, setFlowIdsWithoutFunnel] = useState<Set<string>>(() => new Set());
  const [categoryOptions, setCategoryOptions] = useState<FlowCategoryOption[]>([]);
  const [categoriesFlow, setCategoriesFlow] = useState<FlowListItem | null>(null);
  const [flowSidePanel, setFlowSidePanel] = useState<{
    flow: FlowListItem;
    mode: 'properties' | 'duplicate';
  } | null>(null);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [savingCategories, setSavingCategories] = useState(false);
  const [listTick, setListTick] = useState(0);
  const [importingPackage, setImportingPackage] = useState(false);
  const importPackageInputRef = useRef<HTMLInputElement>(null);
  // Template panel state
  const [templatePanelOpen, setTemplatePanelOpen] = useState(false);
  const [templates, setTemplates] = useState<Array<{ id: string; title: string; description?: string; coverImageUrl?: string; status?: string; updatedAt: string; _count?: { blocks: number } }>>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [creatingFromTemplate, setCreatingFromTemplate] = useState<string | null>(null);
  /** Après choix d’un template : saisie titre/description avant création + ouverture du designer. */
  const [templateNamingMeta, setTemplateNamingMeta] = useState<{ templateId: string; sourceTitle: string } | null>(
    null,
  );
  const [templateFormTitle, setTemplateFormTitle] = useState('');
  const [templateFormDescription, setTemplateFormDescription] = useState('');
  const [templateSearch, setTemplateSearch] = useState('');
  /** null = grille des catégories ; sinon id catégorie ou CATEGORY_BROWSE_UNCATEGORIZED (conservé aussi hors vue catégories pour restauration cookie). */
  const [categoryBrowseKey, setCategoryBrowseKey] = useState<string | null>(() =>
    readEduflowListCategoryBrowseFromCookie(),
  );
  const [editorSharedFlows, setEditorSharedFlows] = useState<FlowListItem[]>([]);

  const loadCategoryOptions = useCallback(async () => {
    try {
      const resp = await api.get<unknown>(institutionCategoriesApiPath());
      const rows = extractInstitutionCategoriesFromApiResponse(resp);
      const normalized = rows
        .map((c: any) => ({
          id: String(c.id),
          name: String(c.name ?? '').trim(),
          color: String(c.color ?? '#3B82F6'),
          description: String(c.description ?? ''),
          coverImageUrl:
            typeof c.coverImageUrl === 'string' && c.coverImageUrl.trim().length > 0 ? c.coverImageUrl.trim() : null,
          nameTranslations: c.nameTranslations,
          descriptionTranslations: c.descriptionTranslations,
        }))
        .filter((c: FlowCategoryOption) => c.id && c.name);
      if (normalized.length > 0) {
        setCategoryOptions(normalized);
        return;
      }
    } catch {
      // fallback local below
    }

    const saved = localStorage.getItem('institution_categories');
    const localRows = saved ? JSON.parse(saved) : [];
    const localNormalized = Array.isArray(localRows)
      ? localRows
          .map((c: any) => ({
            id: String(c.id),
            name: String(c.name ?? '').trim(),
            color: String(c.color ?? '#3B82F6'),
            description: String(c.description ?? ''),
            coverImageUrl:
              typeof c.coverImageUrl === 'string' && c.coverImageUrl.trim().length > 0 ? c.coverImageUrl.trim() : null,
            nameTranslations: c.nameTranslations,
            descriptionTranslations: c.descriptionTranslations,
          }))
          .filter((c: FlowCategoryOption) => c.id && c.name)
      : [];
    setCategoryOptions(localNormalized);
  }, []);

  const filterTabs = useMemo(() => {
    const tabs = [
      { key: 'all', label: t('eduflow.list.filterAll', 'Tous') },
      { key: 'draft', label: t('eduflow.list.filterDraft', 'Brouillons') },
      { key: 'published', label: t('eduflow.list.filterPublished', 'Publiés') },
      { key: 'archived', label: t('eduflow.list.filterArchived', 'Archivés') },
    ];
    if (!isEditorAccount && editorSharedFlows.length > 0) {
      tabs.push({
        key: 'editor_shared',
        label: t('eduflow.list.filterEditorShared', 'Éditeur'),
      });
    }
    return tabs;
  }, [t, isEditorAccount, editorSharedFlows.length]);

  const openCreateFormationPanel = useCallback(() => {
    setNewFormationTitle('');
    setNewFormationDescription('');
    setCreatePanelOpen(true);
  }, []);

  // ─── Template panel helpers ────────────────────────────────────────────────

  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const resp = await api.get<any>('/eduflows/templates', { limit: 100 });
      const rawList = Array.isArray(resp)
        ? resp
        : Array.isArray(resp?.data)
          ? resp.data
          : [];
      setTemplates(rawList);
    } catch {
      setTemplates([]);
      toast.error(t('eduflow.templates.loadError', 'Erreur lors du chargement des templates'));
    } finally {
      setLoadingTemplates(false);
    }
  }, [t]);

  const openTemplatePanelCb = useCallback(() => {
    setTemplateSearch('');
    setTemplateNamingMeta(null);
    setTemplateFormTitle('');
    setTemplateFormDescription('');
    setTemplatePanelOpen(true);
    void fetchTemplates();
  }, [fetchTemplates]);

  const openTemplateNamingStep = useCallback((tpl: { id: string; title: string; description?: string }) => {
    setTemplateNamingMeta({ templateId: tpl.id, sourceTitle: tpl.title });
    setTemplateFormTitle(tpl.title);
    setTemplateFormDescription((tpl.description ?? '').trim());
  }, []);

  const confirmCreateFromTemplate = useCallback(async () => {
    if (!templateNamingMeta) return;
    const title = templateFormTitle.trim();
    if (!title) {
      toast.error(t('eduflow.createFormation.titleRequired'));
      return;
    }
    setCreatingFromTemplate(templateNamingMeta.templateId);
    try {
      const resp = await api.post<any>(`/eduflows/from-template/${templateNamingMeta.templateId}`, {
        title,
        description: templateFormDescription.trim() === '' ? null : templateFormDescription.trim(),
      });
      const id = resp?.data?.id ?? resp?.id;
      if (!id) throw new Error('No id returned');
      setTemplatePanelOpen(false);
      setTemplateNamingMeta(null);
      toast.success(t('eduflow.templates.createSuccess', 'Formation créée depuis le template'));
      navigate(`/eduflow/edit/${id}`);
    } catch (err) {
      console.error('[CreateFromTemplate]', err);
      toast.error(t('eduflow.templates.createError', 'Erreur lors de la création'));
    } finally {
      setCreatingFromTemplate(null);
    }
  }, [navigate, t, templateFormDescription, templateFormTitle, templateNamingMeta]);

  const filteredTemplates = useMemo(() => {
    if (!templateSearch.trim()) return templates;
    const q = templateSearch.toLowerCase();
    return templates.filter(
      (tpl) =>
        tpl.title.toLowerCase().includes(q) ||
        (tpl.description ?? '').toLowerCase().includes(q),
    );
  }, [templates, templateSearch]);

  useEffect(() => {
    if (searchParams.get('new') !== '1') return;
    setNewFormationTitle('');
    setNewFormationDescription('');
    setCreatePanelOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('new');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    void loadCategoryOptions();
  }, [loadCategoryOptions]);

  useEffect(() => {
    if (!createPanelOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !creatingFlow) setCreatePanelOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [createPanelOpen, creatingFlow]);

  useEffect(() => {
    if (!templatePanelOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || creatingFromTemplate) return;
      if (templateNamingMeta) setTemplateNamingMeta(null);
      else setTemplatePanelOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [templatePanelOpen, creatingFromTemplate, templateNamingMeta]);

  /* ---- fetch ---- */
  useEffect(() => {
    let cancelled = false;
    const fetchFlows = async () => {
      try {
        const resp = await api.get<{
          success?: boolean;
          data?: FlowListItem[];
          institutionTrainingNeedShells?: Array<{
            id: string;
            title: string;
            description: string | null;
            updatedAt: string;
          }>;
        }>('/eduflows');
        if (!cancelled) {
          const rawList = Array.isArray(resp)
            ? resp
            : Array.isArray((resp as { data?: FlowListItem[] }).data)
              ? (resp as { data: FlowListItem[] }).data
              : [];
          const shells = Array.isArray((resp as { institutionTrainingNeedShells?: unknown }).institutionTrainingNeedShells)
            ? (resp as { institutionTrainingNeedShells: Array<{ id: string; title: string; description: string | null; updatedAt: string }> })
                .institutionTrainingNeedShells
            : [];
          const shellFlows: FlowListItem[] = shells.map((s) => ({
            id: s.id,
            title: s.title,
            description: s.description ?? undefined,
            status: 'draft',
            blocks: [],
            categories: [],
            updatedAt: s.updatedAt,
            listKind: 'institution_shell',
            fromInstitutionTrainingNeed: true,
          }));
          const eduFlows: FlowListItem[] = (rawList as FlowListItem[]).map((f) => ({
            ...f,
            listKind: 'eduflow',
          }));
          setFlows(
            [...shellFlows, ...eduFlows].sort(
              (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
            ),
          );
        }
      } catch {
        if (!cancelled) setFlows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchFlows();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isLyrraOne()) return;
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchAvailableFormations();
        if (!cancelled) {
          setFlowIdsWithoutFunnel(new Set(rows.map((r) => r.flowId)));
        }
      } catch {
        if (!cancelled) setFlowIdsWithoutFunnel(new Set());
      }
    })();
    return () => { cancelled = true; };
  }, [listTick]);

  useEffect(() => {
    if (isEditorAccount) {
      setEditorSharedFlows([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows = await listTrainerEditorSharedEduflows();
        if (cancelled) return;
        const mapped: FlowListItem[] = rows.map((row) => ({
          id: row.content.id,
          title: row.content.title,
          coverImageUrl: row.content.coverImageUrl ?? null,
          status:
            row.content.status === 'draft' ||
            row.content.status === 'published' ||
            row.content.status === 'archived'
              ? row.content.status
              : 'published',
          blocks: [],
          updatedAt: row.content.updatedAt,
          listKind: 'editor_shared',
          editorName: row.editor.name,
          shareId: row.shareId,
        }));
        setEditorSharedFlows(mapped);
      } catch {
        if (!cancelled) setEditorSharedFlows([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEditorAccount]);

  /* ---- derived ---- */
  const filteredFlows = useMemo(() => {
    if (activeFilter === 'editor_shared') {
      let result = editorSharedFlows;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        result = result.filter(
          (f) =>
            f.title.toLowerCase().includes(q) ||
            f.editorName?.toLowerCase().includes(q),
        );
      }
      return result;
    }

    let result = flows;
    if (activeFilter === 'all') {
      /* Les archivés ne sont visibles que sous l’onglet « Archivés ». */
      result = result.filter((f) => f.listKind === 'institution_shell' || f.status !== 'archived');
    } else if (activeFilter !== 'all') {
      result = result.filter((f) => {
        if (f.listKind === 'institution_shell') return activeFilter === 'draft';
        return f.status === activeFilter;
      });
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (f) =>
          f.title.toLowerCase().includes(q) ||
          f.description?.toLowerCase().includes(q),
      );
    }
    return result;
  }, [flows, editorSharedFlows, activeFilter, searchQuery]);

  const groupedByCategory = useMemo(() => {
    const metaById = new Map(categoryOptions.map((c) => [c.id, c] as const));
    const map = new Map<string, { category: CategoryGroupDisplay; flows: FlowListItem[] }>();
    const uncategorized: FlowListItem[] = [];

    for (const flow of filteredFlows) {
      const cats = Array.isArray(flow.categories) ? flow.categories : [];
      if (cats.length === 0) {
        uncategorized.push(flow);
        continue;
      }
      for (const cat of cats) {
        if (!cat?.id) continue;
        if (!map.has(cat.id)) {
          const meta = metaById.get(cat.id);
          const merged = {
            name: cat.name,
            nameTranslations: cat.nameTranslations ?? meta?.nameTranslations,
            description: cat.description ?? meta?.description ?? '',
            descriptionTranslations: cat.descriptionTranslations ?? meta?.descriptionTranslations,
          };
          const descFromFlow = typeof cat.description === 'string' ? cat.description.trim() : '';
          const coverFromFlow =
            typeof cat.coverImageUrl === 'string' && cat.coverImageUrl.trim().length > 0 ? cat.coverImageUrl.trim() : null;
          map.set(cat.id, {
            category: {
              id: cat.id,
              name: localizedInstitutionCategoryName(merged, i18n.language),
              color: cat.color ?? meta?.color ?? '#94a3b8',
              description: localizedInstitutionCategoryDescription(
                {
                  description: (meta?.description?.trim() || descFromFlow || '').trim() || null,
                  descriptionTranslations: merged.descriptionTranslations,
                  name: merged.name,
                  nameTranslations: merged.nameTranslations,
                },
                i18n.language,
              ),
              coverImageUrl: meta?.coverImageUrl ?? coverFromFlow ?? null,
            },
            flows: [],
          });
        }
        map.get(cat.id)!.flows.push(flow);
      }
    }

    for (const opt of categoryOptions) {
      if (!map.has(opt.id)) {
        map.set(opt.id, {
          category: {
            id: opt.id,
            name: localizedInstitutionCategoryName(opt, i18n.language),
            color: opt.color,
            description: localizedInstitutionCategoryDescription(opt, i18n.language),
            coverImageUrl: opt.coverImageUrl ?? null,
          },
          flows: [],
        });
      }
    }

    const groups = Array.from(map.values()).sort((a, b) =>
      a.category.name.localeCompare(b.category.name, i18n.language, { sensitivity: 'base' }),
    );
    return { groups, uncategorized };
  }, [filteredFlows, categoryOptions, i18n.language]);

  /** En liste / grille, on n’affiche pas le zoom catégorie mais on garde la clé en state pour le cookie et le retour sur « catégories ». */
  const categoryKeyForUi = viewMode === 'categories' ? categoryBrowseKey : null;

  useEffect(() => {
    setCookie(EDUFLOW_LIST_VIEW_MODE_COOKIE, viewMode);
  }, [viewMode]);

  useEffect(() => {
    if (categoryBrowseKey === null) setCookie(EDUFLOW_LIST_CATEGORY_BROWSE_COOKIE, '');
    else setCookie(EDUFLOW_LIST_CATEGORY_BROWSE_COOKIE, categoryBrowseKey);
  }, [categoryBrowseKey]);

  const activeCategoryBrowse = useMemo(() => {
    if (categoryKeyForUi === null) return null;
    if (categoryKeyForUi === CATEGORY_BROWSE_UNCATEGORIZED) {
      return {
        category: null as CategoryGroupDisplay | null,
        flows: groupedByCategory.uncategorized,
      };
    }
    const g = groupedByCategory.groups.find((x) => x.category.id === categoryKeyForUi);
    if (!g) return null;
    return { category: g.category, flows: g.flows };
  }, [categoryKeyForUi, groupedByCategory]);

  useEffect(() => {
    if (categoryBrowseKey === null || categoryBrowseKey === CATEGORY_BROWSE_UNCATEGORIZED) return;
    const exists = groupedByCategory.groups.some((g) => g.category.id === categoryBrowseKey);
    if (!exists) setCategoryBrowseKey(null);
  }, [categoryBrowseKey, groupedByCategory.groups]);

  /* ---- handlers ---- */
  const submitCreateFormation = async () => {
    const title = newFormationTitle.trim();
    if (!title) {
      toast.error(t('eduflow.createFormation.titleRequired'));
      return;
    }
    setCreatingFlow(true);
    try {
      const categoryIds =
        viewMode === 'categories' &&
        categoryBrowseKey &&
        categoryBrowseKey !== CATEGORY_BROWSE_UNCATEGORIZED &&
        CATEGORY_BROWSE_UUID_RE.test(categoryBrowseKey)
          ? [categoryBrowseKey]
          : undefined;
      const resp = await api.post<any>('/eduflows', {
        title,
        description: newFormationDescription.trim() || undefined,
        creationMode: createMode,
        ...(categoryIds ? { categoryIds } : {}),
      });
      const id = resp?.data?.id ?? resp?.id;
      if (!id) throw new Error('No id returned');
      setCreatePanelOpen(false);
      navigate(createMode === 'simple' ? `/eduflow/simple/${id}/structure` : `/eduflow/edit/${id}`);
    } catch (err) {
      console.error('[CreateFormation]', err);
      toast.error(t('eduflow.createFormation.error'));
    } finally {
      setCreatingFlow(false);
    }
  };

  const handleImportPick = useCallback(() => {
    importPackageInputRef.current?.click();
  }, []);

  const handleExportJsonLight = useCallback(
    async (flow: FlowListItem) => {
      try {
        const blob = await eduflowApi.exportFlowJsonLight(flow.id);
        const safeName =
          flow.title
            .trim()
            .replace(/[^\w\s-àâäéèêëïîôùûüçœæÀÂÄÉÈÊËÏÎÔÙÛÜÇŒÆ]/gi, '')
            .replace(/\s+/g, '-')
            .slice(0, 80) || flow.id;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${safeName}.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(
          t(
            'eduflow.list.exportJsonLightSuccess',
            'Export JSON téléchargé (sans images ni vidéos).',
          ),
        );
      } catch (err) {
        console.error('[ExportJsonLight]', err);
        toast.error(t('eduflow.list.exportJsonLightError', 'Export JSON impossible.'));
      }
    },
    [t],
  );

  const handleImportChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target;
      const file = input.files?.[0];
      input.value = '';
      if (!file) return;
      setImportingPackage(true);
      try {
        const lower = file.name.toLowerCase();
        const isLyrraPackage =
          lower.endsWith('.lyrra') ||
          file.type === 'application/zip' ||
          file.type === 'application/x-zip-compressed';

        if (isLyrraPackage) {
          const resp = (await eduflowApi.importPackage(file)) as {
            data?: { id?: string };
          };
          const id = resp?.data?.id != null ? String(resp.data.id) : '';
          if (!id) throw new Error('no id');
          toast.success(t('eduflow.list.importSuccess', 'Formation importée. Le designer s’ouvre.'));
          navigate(buildEduFlowEditUrl(id, formateurFlowsListPath()));
          return;
        }

        const text = await file.text();
        const payload = JSON.parse(text) as unknown;
        const payloadObj =
          payload && typeof payload === 'object'
            ? (payload as { format?: string })
            : null;

        if (payloadObj?.format === 'lyrraone-formation-json-light') {
          const resp = (await eduflowApi.importFlowJsonLight(payload)) as {
            data?: { id?: string; creationMode?: 'simple' | 'advanced' };
            id?: string;
            creationMode?: 'simple' | 'advanced';
          };
          const inner = resp?.data ?? resp;
          const id = inner && typeof inner === 'object' && inner.id != null ? String(inner.id) : '';
          const creationMode =
            inner && typeof inner === 'object' ? inner.creationMode : undefined;
          if (!id) throw new Error('no id');
          toast.success(t('eduflow.list.importSuccess', 'Formation importée. Le designer s’ouvre.'));
          navigate(
            creationMode === 'simple'
              ? `/eduflow/simple/${id}/structure`
              : buildEduFlowEditUrl(id, formateurFlowsListPath()),
          );
          return;
        }

        const resp = (await eduflowApi.importFlow(payload)) as {
          data?: { id?: string };
          id?: string;
        };
        const inner = resp?.data ?? resp;
        const id = inner && typeof inner === 'object' && inner.id != null ? String(inner.id) : '';
        if (!id) throw new Error('no id');
        toast.success(t('eduflow.list.importSuccess', 'Formation importée. Le designer s’ouvre.'));
        navigate(buildEduFlowEditUrl(id, formateurFlowsListPath()));
      } catch (err) {
        console.error('[ImportFormation]', err);
        toast.error(
          t(
            'eduflow.list.importError',
            'Import impossible — vérifiez le fichier (.lyrra ou export JSON valide).',
          ),
        );
      } finally {
        setImportingPackage(false);
      }
    },
    [navigate, t],
  );

  const [flowPendingDelete, setFlowPendingDelete] = useState<FlowListItem | null>(null);

  const confirmDeleteFlow = async () => {
    const target = flowPendingDelete;
    if (!target || target.listKind === 'institution_shell') {
      setFlowPendingDelete(null);
      return;
    }
    /* Coquilles « besoin entreprise » : `institution_shell` ; EduFlow lié catalogue : flag ci-dessous. */
    if (target.fromInstitutionTrainingNeed) {
      setFlowPendingDelete(null);
      return;
    }
    const id = target.id;
    const prev = [...flows];
    setFlows((f) => f.filter((fl) => fl.id !== id));
    setFlowPendingDelete(null);
    try {
      await api.delete(`/eduflows/${id}`);
    } catch {
      setFlows(prev);
    }
  };

  const openDuplicateFlowPanel = (flow: FlowListItem) => {
    if (flow.listKind === 'institution_shell') return;
    setFlowSidePanel({ flow, mode: 'duplicate' });
  };

  const handleTogglePublish = async (flow: FlowListItem) => {
    if (flow.listKind === 'institution_shell') return;
    if (flow.status === 'archived') return;
    const newStatus: FlowListItem['status'] =
      flow.status === 'published' ? 'draft' : 'published';
    if (newStatus === 'published') {
      const allowed = await ensureSellerBillingForPublish();
      if (!allowed) return;
      if (isLyrraOne() && flow.status !== 'published' && quota('publishedFormations').atLimit) {
        toast.error('Limite de formations publiées atteinte pour votre plan. Passez au plan Créateur pour publier davantage.');
        return;
      }
    }
    setFlows((f) =>
      f.map((fl) => (fl.id === flow.id ? { ...fl, status: newStatus } : fl)),
    );
    try {
      if (newStatus === 'published') {
        await api.post(`/eduflows/${flow.id}/publish`);
      } else {
        await api.post(`/eduflows/${flow.id}/unpublish`);
      }
    } catch (err: unknown) {
      setFlows((f) =>
        f.map((fl) =>
          fl.id === flow.id ? { ...fl, status: flow.status } : fl,
        ),
      );
      notifyEduFlowPublishError(
        err,
        newStatus === 'published'
          ? t('eduflow.list.publishError', 'Impossible de publier la formation.')
          : t('eduflow.list.unpublishError', 'Impossible de dépublier la formation.'),
      );
    }
  };

  const handleArchiveFlow = async (flow: FlowListItem) => {
    if (flow.listKind === 'institution_shell' || flow.status === 'archived') return;
    const prev = flow.status;
    setFlows((f) => f.map((fl) => (fl.id === flow.id ? { ...fl, status: 'archived' } : fl)));
    try {
      await api.put(`/eduflows/${flow.id}`, { status: 'archived' });
      toast.success(t('eduflow.list.archiveSuccess', 'Formation archivée'));
    } catch {
      setFlows((f) => f.map((fl) => (fl.id === flow.id ? { ...fl, status: prev } : fl)));
      toast.error(t('eduflow.list.archiveError', 'Impossible d’archiver la formation.'));
    }
  };

  const handleRestoreArchivedFlow = async (flow: FlowListItem) => {
    if (flow.listKind === 'institution_shell' || flow.status !== 'archived') return;
    setFlows((f) => f.map((fl) => (fl.id === flow.id ? { ...fl, status: 'draft' } : fl)));
    try {
      await api.put(`/eduflows/${flow.id}`, { status: 'draft' });
      toast.success(t('eduflow.list.restoreSuccess', 'Formation restaurée en brouillon'));
    } catch {
      setFlows((f) => f.map((fl) => (fl.id === flow.id ? { ...fl, status: 'archived' } : fl)));
      toast.error(t('eduflow.list.restoreError', 'Impossible de restaurer la formation.'));
    }
  };

  const handleStartShellFlow = useCallback(async (shellId: string) => {
    try {
      const res = await api.post<unknown>(`/institution/content/catalog-items/${shellId}/start-eduflow`, {});
      const data =
        res != null && typeof res === 'object' && 'data' in res && (res as { data: unknown }).data !== undefined
          ? (res as { data: { flowId?: string } }).data
          : (res as { flowId?: string });
      if (data?.flowId) {
        navigate(`/eduflow/edit/${data.flowId}`);
        return;
      }
      throw new Error('flowId manquant');
    } catch {
      toast.error("Impossible d'ouvrir cette formation pour le moment. Ouvrez Mes missions.");
      navigate('/producer-assignments');
    }
  }, [navigate]);

  const resolveFlowOpen = useCallback(
    (flow: FlowListItem) => {
      if (flow.listKind === 'institution_shell') {
        void handleStartShellFlow(flow.id);
        return;
      }
      if (flow.listKind === 'editor_shared') {
        navigate(`/eduflow/flows/${flow.id}/play`);
        return;
      }
      navigate(getEduFlowEditPath(flow));
    },
    [navigate, handleStartShellFlow],
  );

  const openFormationDashboard = useCallback(
    (flow: FlowListItem) => {
      if (flow.listKind === 'institution_shell' || flow.listKind === 'editor_shared') return;
      navigate(formateurFormationDashboardPath(flow.id));
    },
    [navigate],
  );

  const handleSaveTitle = async (id: string) => {
    const target = flows.find((f) => f.id === id);
    if (target?.listKind === 'institution_shell') {
      setEditingId(null);
      return;
    }
    if (!editingTitle.trim()) {
      setEditingId(null);
      return;
    }
    setFlows((f) =>
      f.map((fl) => (fl.id === id ? { ...fl, title: editingTitle } : fl)),
    );
    setEditingId(null);
    try {
      await api.put(`/eduflows/${id}`, { title: editingTitle });
    } catch {
      /* title already updated locally */
    }
  };

  const handleStartEditing = (flow: FlowListItem) => {
    if (flow.listKind === 'institution_shell') return;
    setEditingId(flow.id);
    setEditingTitle(flow.title);
  };

  const openCategoriesPanel = (flow: FlowListItem) => {
    if (flow.listKind === 'institution_shell') return;
    void loadCategoryOptions();
    setCategoriesFlow(flow);
    setSelectedCategoryIds((flow.categories ?? []).map((c) => c.id));
  };

  const toggleCategorySelection = (id: string) => {
    setSelectedCategoryIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const saveFlowCategories = async () => {
    if (!categoriesFlow) return;
    setSavingCategories(true);
    const categoryMap = new Map(categoryOptions.map((c) => [c.id, c] as const));
    const optimisticCategories = selectedCategoryIds
      .map((id) => categoryMap.get(id))
      .filter(Boolean)
      .map((c) => ({ id: c!.id, name: c!.name, color: c!.color }));
    const previous = flows;
    setFlows((prev) => prev.map((f) => (f.id === categoriesFlow.id ? { ...f, categories: optimisticCategories } : f)));
    try {
      await api.put(`/eduflows/${categoriesFlow.id}`, { categoryIds: selectedCategoryIds });
      setCategoriesFlow(null);
    } catch {
      setFlows(previous);
      toast.error(t('eduflow.list.categoriesSaveError', 'Impossible de sauvegarder les catégories.'));
    } finally {
      setSavingCategories(false);
    }
  };

  /* ---- loading state ---- */
  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[color:var(--inst-player-primary,#0C1C38)]" />
      </div>
    );
  }

  /* ---- render ---- */
  return (
    <div className={cn('space-y-6', embedded && 'px-6 py-6')} data-tour="tour-eduflow-list-root">
      {/* header */}
      <div
        className={cn(
          'flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between',
          embedded && 'sm:justify-end',
        )}
      >
        {!embedded ? (
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_12%,transparent)]">
            <Workflow className="h-5 w-5 text-[color:var(--inst-player-primary,#0C1C38)]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{t('eduflow.list.title')}</h1>
            <p className="text-sm text-slate-500 dark:text-gray-400">{t('eduflow.list.subtitle')}</p>
          </div>
        </div>
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={importPackageInputRef}
            type="file"
            accept=".lyrra,.json,.eduflow.json,application/json,application/zip,application/x-zip-compressed"
            className="hidden"
            aria-hidden
            onChange={handleImportChange}
          />
          <button
            type="button"
            onClick={handleImportPick}
            disabled={importingPackage}
            title={t(
              'eduflow.list.importHint',
              'Fichier .lyrra (recommandé) ou export JSON historique Lyrra V1.',
            )}
            className={cn(
              'inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm',
              'hover:border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_35%,transparent)] hover:bg-slate-50',
              'focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_25%,transparent)] focus:ring-offset-2',
              'transition-all duration-200 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100',
            )}
          >
            {importingPackage ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4 text-[color:var(--inst-player-primary,#0C1C38)]" />
            )}
            {t('eduflow.list.import', 'Importer')}
          </button>
          <button
            type="button"
            onClick={openCreateFormationPanel}
            disabled={creatingFlow}
            className={cn(
              'inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-sm',
              'bg-[color:var(--inst-player-primary,#0C1C38)] hover:bg-[color:var(--inst-player-primary-hover,#19335E)] focus:outline-none focus:ring-2 focus:ring-[color:var(--inst-player-primary,#0C1C38)] focus:ring-offset-2',
              'transition-all duration-200 disabled:opacity-60',
            )}
          >
            {creatingFlow ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            {t('eduflow.createFormation.panelTitle')}
          </button>
          <button
            type="button"
            onClick={openTemplatePanelCb}
            className={cn(
              'inline-flex items-center gap-2 rounded-xl border-2 border-[color:var(--inst-player-primary,#0C1C38)] px-4 py-2.5 text-sm font-semibold shadow-sm',
              'text-[color:var(--inst-player-primary,#0C1C38)] bg-white hover:bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_8%,white)]',
              'focus:outline-none focus:ring-2 focus:ring-[color:var(--inst-player-primary,#0C1C38)] focus:ring-offset-2',
              'transition-all duration-200 dark:bg-gray-900 dark:text-emerald-400 dark:border-emerald-400 dark:hover:bg-gray-800',
            )}
          >
            <BookTemplate className="h-4 w-4" />
            {t('eduflow.templates.createFrom', 'Depuis un template')}
          </button>
        </div>
      </div>

      {isLyrraOne() ? (
        <LyrraOnePlanQuotaBanner limitKey="publishedFormations" className="mb-2" />
      ) : null}

      {/* toolbar: filters + search + view toggle */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-1 rounded-lg bg-slate-100 dark:bg-gray-800 p-1">
          {filterTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveFilter(tab.key)}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-all duration-200',
                activeFilter === tab.key
                  ? 'bg-white dark:bg-gray-700 text-slate-900 dark:text-white shadow-sm'
                  : 'text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <div className="w-64 min-w-0 shrink-0">
            <InstitutionStyledSearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('eduflow.list.searchPlaceholder')}
            />
          </div>

          <div className="flex items-center rounded-lg border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-0.5">
            <Tooltip
              content={t('eduflow.list.viewModeGrid', 'Vue mosaïque')}
              position="top"
              delay={280}
              className="max-w-[min(220px,calc(100vw-24px))] px-3 py-2 text-[13px] leading-snug shadow-xl ring-1 ring-white/15 dark:ring-black/15"
            >
              <button
                type="button"
                aria-label={t('eduflow.list.viewModeGrid', 'Vue mosaïque')}
                onClick={() => setViewMode('grid')}
                className={cn(
                  'rounded-md p-1.5 transition-all duration-200',
                  viewMode === 'grid'
                    ? 'bg-slate-100 dark:bg-gray-700 text-slate-900 dark:text-white'
                    : 'text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300',
                )}
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
            </Tooltip>
            <Tooltip
              content={t('eduflow.list.viewModeList', 'Vue liste')}
              position="top"
              delay={280}
              className="max-w-[min(220px,calc(100vw-24px))] px-3 py-2 text-[13px] leading-snug shadow-xl ring-1 ring-white/15 dark:ring-black/15"
            >
              <button
                type="button"
                aria-label={t('eduflow.list.viewModeList', 'Vue liste')}
                onClick={() => setViewMode('list')}
                className={cn(
                  'rounded-md p-1.5 transition-all duration-200',
                  viewMode === 'list'
                    ? 'bg-slate-100 dark:bg-gray-700 text-slate-900 dark:text-white'
                    : 'text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300',
                )}
              >
                <List className="h-4 w-4" />
              </button>
            </Tooltip>
            <Tooltip
              content={t('eduflow.list.viewByCategory', 'Vue catégories')}
              position="top"
              delay={280}
              className="max-w-[min(220px,calc(100vw-24px))] px-3 py-2 text-[13px] leading-snug shadow-xl ring-1 ring-white/15 dark:ring-black/15"
            >
              <button
                type="button"
                aria-label={t('eduflow.list.viewByCategory', 'Vue catégories')}
                onClick={() => setViewMode('categories')}
                className={cn(
                  'rounded-md p-1.5 transition-all duration-200',
                  viewMode === 'categories'
                    ? 'bg-slate-100 dark:bg-gray-700 text-slate-900 dark:text-white'
                    : 'text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300',
                )}
              >
                <FolderTree className="h-4 w-4" />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* cards / empty state */}
      {filteredFlows.length === 0 ? (
        <EmptyState
          hasFlows={
            activeFilter === 'editor_shared' ? editorSharedFlows.length > 0 : flows.length > 0
          }
          onOpenCreatePanel={activeFilter === 'editor_shared' ? undefined : openCreateFormationPanel}
          creatingFlow={creatingFlow}
          emptyTitle={
            activeFilter === 'editor_shared'
              ? t('eduflow.list.emptyEditorFilteredTitle', 'Aucune formation éditeur')
              : undefined
          }
          emptyHint={
            activeFilter === 'editor_shared'
              ? t(
                  'eduflow.list.emptyEditorFilteredHint',
                  'Aucun résultat pour cette recherche, ou aucun éditeur n’a partagé de formation avec votre établissement.',
                )
              : undefined
          }
        />
      ) : viewMode === 'categories' ? (
        <div className="space-y-5">
          {categoryKeyForUi !== null && activeCategoryBrowse ? (
            <>
              <button
                type="button"
                onClick={() => setCategoryBrowseKey(null)}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-gray-600 dark:bg-gray-800 dark:text-slate-200 dark:hover:bg-gray-700"
              >
                <ChevronLeft className={cn('h-4 w-4', rtlMirrorHorizontalChevron)} />
                {t('eduflow.list.categoryBack', 'Toutes les catégories')}
              </button>

              <div className="rounded-xl border border-slate-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
                {activeCategoryBrowse.category ? (
                  <>
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                      <div className="relative h-36 w-full shrink-0 overflow-hidden rounded-xl bg-slate-100 dark:bg-gray-800 sm:h-32 sm:w-48">
                        {activeCategoryBrowse.category.coverImageUrl ? (
                          <OptimizedImage
                            priority
                            src={imagesApi.resolveUrl(activeCategoryBrowse.category.coverImageUrl) ?? ''}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div
                            className="flex h-full w-full items-center justify-center"
                            style={{
                              background: `linear-gradient(135deg, ${activeCategoryBrowse.category.color}55, ${activeCategoryBrowse.category.color}18)`,
                            }}
                          >
                            <FolderTree className="h-14 w-14 opacity-50" style={{ color: activeCategoryBrowse.category.color }} />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className="h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: activeCategoryBrowse.category.color }}
                            aria-hidden
                          />
                          <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                            {activeCategoryBrowse.category.name}
                          </h2>
                        </div>
                        {activeCategoryBrowse.category.description ? (
                          <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                            {activeCategoryBrowse.category.description}
                          </p>
                        ) : null}
                        <p className="mt-2 text-xs font-medium text-slate-500 dark:text-slate-500">
                          {t('eduflow.list.categoryFormationCount', { count: activeCategoryBrowse.flows.length })}
                        </p>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <Tag className="h-5 w-5 text-slate-500 dark:text-gray-400" />
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                      {t('eduflow.list.uncategorized', 'Sans catégorie')}
                    </h2>
                    <span className="text-xs text-slate-500 dark:text-gray-400">
                      {t('eduflow.list.categoryFormationCount', { count: activeCategoryBrowse.flows.length })}
                    </span>
                  </div>
                )}
              </div>

              {activeCategoryBrowse.flows.length === 0 ? (
                <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-8 text-center text-sm text-slate-600 dark:border-gray-600 dark:bg-gray-900/40 dark:text-slate-400">
                  {t(
                    'eduflow.list.categoryEmptyFiltered',
                    'Aucune formation dans cette vue (filtre ou recherche actifs).',
                  )}
                </p>
              ) : (
                <div className="flex flex-col gap-3">
                  {activeCategoryBrowse.flows.map((flow, index) => (
                    <FlowCard
                      key={`browse-${flow.id}-${index}`}
                      flow={flow}
                      categoryUiLang={i18n.language}
                      isCollaborative={Boolean(user?.id && flow.userId && flow.userId !== user.id)}
                      collaborativeLabel={t('eduflow.collaborativeBadge', 'Collaboratif')}
                      index={index}
                      viewMode="list"
                      isEditing={editingId === flow.id}
                      editingTitle={editingTitle}
                      onEditTitle={() => handleStartEditing(flow)}
                      onTitleChange={setEditingTitle}
                      onSaveTitle={() => handleSaveTitle(flow.id)}
                      onCancelEdit={() => setEditingId(null)}
                      onEdit={() => resolveFlowOpen(flow)}
                      onDuplicate={() => openDuplicateFlowPanel(flow)}
                      onExportJson={
                        flow.listKind === 'institution_shell' || flow.listKind === 'editor_shared'
                          ? undefined
                          : () => void handleExportJsonLight(flow)
                      }
                      onTogglePublish={() => handleTogglePublish(flow)}
                      hidePublish={isEditorAccount}
                      onArchive={() => void handleArchiveFlow(flow)}
                      onRestoreArchive={() => void handleRestoreArchivedFlow(flow)}
                      onDelete={() => setFlowPendingDelete(flow)}
                      onOpenOffer={() => setOfferFlow(flow)}
                      onGenerateFunnelAi={
                        canGenerateFunnelAi && flowIdsWithoutFunnel.has(flow.id)
                          ? () => setFunnelAiFlow(flow)
                          : undefined
                      }
                      onManageCategories={() => openCategoriesPanel(flow)}
                      onOpenProperties={() => setFlowSidePanel({ flow, mode: 'properties' })}
                      onOpenDashboard={
                        flow.listKind === 'institution_shell' || flow.listKind === 'editor_shared'
                          ? undefined
                          : () => openFormationDashboard(flow)
                      }
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t('eduflow.list.categoryBrowseHint', 'Choisissez une catégorie pour afficher ses formations.')}
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {groupedByCategory.groups.map((group) => (
                  <button
                    key={group.category.id}
                    type="button"
                    onClick={() => setCategoryBrowseKey(group.category.id)}
                    className="group flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white text-start shadow-sm transition-all hover:border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_40%,transparent)] hover:shadow-md dark:border-gray-700 dark:bg-gray-900"
                  >
                    <div className="relative h-44 w-full overflow-hidden bg-slate-100 dark:bg-gray-800">
                      {group.category.coverImageUrl ? (
                        <OptimizedImage
                          src={imagesApi.resolveUrl(group.category.coverImageUrl) ?? ''}
                          alt=""
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                        />
                      ) : (
                        <div
                          className="flex h-full w-full items-center justify-center"
                          style={{
                            background: `linear-gradient(135deg, ${group.category.color}55, ${group.category.color}18)`,
                          }}
                        >
                          <FolderTree className="h-12 w-12 opacity-45" style={{ color: group.category.color }} />
                        </div>
                      )}
                    </div>
                    <div className="flex flex-1 flex-col p-4">
                      <div className="flex items-center gap-2">
                        <span
                          className="h-2 w-2 shrink-0 rounded-full"
                          style={{ backgroundColor: group.category.color }}
                          aria-hidden
                        />
                        <h3 className="font-semibold text-slate-900 dark:text-white">{group.category.name}</h3>
                      </div>
                      {group.category.description ? (
                        <p className="mt-2 line-clamp-3 text-sm text-slate-600 dark:text-slate-400">
                          {group.category.description}
                        </p>
                      ) : null}
                      <p className="mt-auto pt-3 text-xs font-semibold text-[color:var(--inst-player-primary,#0C1C38)]">
                        {t('eduflow.list.categoryFormationCount', { count: group.flows.length })}
                      </p>
                    </div>
                  </button>
                ))}

                {groupedByCategory.uncategorized.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => setCategoryBrowseKey(CATEGORY_BROWSE_UNCATEGORIZED)}
                    className="group flex h-full flex-col overflow-hidden rounded-xl border border-dashed border-slate-300 bg-slate-50/80 text-start shadow-sm transition-all hover:border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_45%,transparent)] hover:bg-white dark:border-gray-600 dark:bg-gray-900/50 dark:hover:bg-gray-900"
                  >
                    <div className="relative flex h-44 w-full items-center justify-center bg-slate-100 dark:bg-gray-800/80">
                      <Tag className="h-12 w-12 text-slate-400 dark:text-gray-500" />
                    </div>
                    <div className="flex flex-1 flex-col p-4">
                      <h3 className="font-semibold text-slate-800 dark:text-gray-100">
                        {t('eduflow.list.uncategorized', 'Sans catégorie')}
                      </h3>
                      <p className="mt-2 line-clamp-2 text-sm text-slate-600 dark:text-slate-400">
                        {t('eduflow.list.uncategorizedCardHint', 'Formations sans étiquette de catégorie.')}
                      </p>
                      <p className="mt-auto pt-3 text-xs font-semibold text-[color:var(--inst-player-primary,#0C1C38)]">
                        {t('eduflow.list.categoryFormationCount', { count: groupedByCategory.uncategorized.length })}
                      </p>
                    </div>
                  </button>
                ) : null}
              </div>
            </>
          )}
        </div>
      ) : (
        <motion.div
          className={cn(
            viewMode === 'grid'
              ? 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
              : 'lo-fc-list',
          )}
        >
          <AnimatePresence mode="popLayout">
            {filteredFlows.map((flow, index) => (
              <FlowCard
                key={flow.id}
                flow={flow}
                categoryUiLang={i18n.language}
                isCollaborative={Boolean(user?.id && flow.userId && flow.userId !== user.id)}
                collaborativeLabel={t('eduflow.collaborativeBadge', 'Collaboratif')}
                index={index}
                viewMode={viewMode}
                isEditing={editingId === flow.id}
                editingTitle={editingTitle}
                onEditTitle={() => handleStartEditing(flow)}
                onTitleChange={setEditingTitle}
                onSaveTitle={() => handleSaveTitle(flow.id)}
                onCancelEdit={() => setEditingId(null)}
                onEdit={() => resolveFlowOpen(flow)}
                onDuplicate={() => openDuplicateFlowPanel(flow)}
                onExportJson={
                  flow.listKind === 'institution_shell' || flow.listKind === 'editor_shared'
                    ? undefined
                    : () => void handleExportJsonLight(flow)
                }
                onTogglePublish={() => handleTogglePublish(flow)}
                hidePublish={isEditorAccount}
                onArchive={() => void handleArchiveFlow(flow)}
                onRestoreArchive={() => void handleRestoreArchivedFlow(flow)}
                onDelete={() => setFlowPendingDelete(flow)}
                onOpenOffer={() => setOfferFlow(flow)}
                onGenerateFunnelAi={
                  canGenerateFunnelAi && flowIdsWithoutFunnel.has(flow.id)
                    ? () => setFunnelAiFlow(flow)
                    : undefined
                }
                onManageCategories={() => openCategoriesPanel(flow)}
                onOpenProperties={() => setFlowSidePanel({ flow, mode: 'properties' })}
                onOpenDashboard={
                  flow.listKind === 'institution_shell' || flow.listKind === 'editor_shared'
                    ? undefined
                    : () => openFormationDashboard(flow)
                }
              />
            ))}
          </AnimatePresence>
        </motion.div>
      )}

      <Modal
        open={
          !!flowPendingDelete &&
          flowPendingDelete.listKind !== 'institution_shell' &&
          !flowPendingDelete.fromInstitutionTrainingNeed
        }
        onClose={() => setFlowPendingDelete(null)}
        size="sm"
        ariaLabelledBy="delete-flow-title"
      >
        <ModalHeader>
          <h2 id="delete-flow-title" className="text-base font-semibold text-[color:var(--inst-player-text,#111827)] dark:text-gray-100">
            {t('eduflow.list.deleteToTrashTitle')}
          </h2>
        </ModalHeader>
        <ModalBody>
          <p className="text-sm text-[color:var(--inst-player-text-muted,#6b7280)] dark:text-gray-400">
            {flowPendingDelete
              ? t('eduflow.list.deleteToTrashBody', { title: flowPendingDelete.title })
              : ''}
          </p>
        </ModalBody>
        <ModalFooter className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => setFlowPendingDelete(null)}>
            {t('btn.cancel')}
          </Button>
          <Button variant="destructive" onClick={() => void confirmDeleteFlow()}>
            {t('eduflow.list.deleteToTrashConfirm')}
          </Button>
        </ModalFooter>
      </Modal>

      {createPanelOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <>
            <div
              className={panelBackdropClass}
              onClick={() => !creatingFlow && setCreatePanelOpen(false)}
              aria-hidden
            />
            <div
              className={panelShellClass}
              role="dialog"
              aria-modal="true"
              aria-labelledby="create-formation-panel-title"
            >
              <TrainerRightPanelHeader
                icon={Workflow}
                title={t('eduflow.createFormation.panelTitle')}
                titleId="create-formation-panel-title"
                description={t('eduflow.createFormation.panelSubtitle')}
                onClose={() => !creatingFlow && setCreatePanelOpen(false)}
                closeDisabled={creatingFlow}
                closeAriaLabel={t('btn.close')}
              />
              <div className="flex flex-1 flex-col overflow-y-auto p-6">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setCreateMode('simple')}
                      className={cn(
                        'rounded-xl border p-3 text-left text-sm transition-colors',
                        createMode === 'simple'
                          ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200 dark:bg-primary-950/30'
                          : 'border-gray-200 hover:border-gray-300 dark:border-gray-700',
                      )}
                    >
                      <p className="font-semibold text-gray-900 dark:text-white">Simple</p>
                      <p className="mt-1 text-xs text-gray-500">Modules, chapitres et blocs vidéo / texte / audio / quiz</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setCreateMode('advanced')}
                      className={cn(
                        'rounded-xl border p-3 text-left text-sm transition-colors',
                        createMode === 'advanced'
                          ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200 dark:bg-primary-950/30'
                          : 'border-gray-200 hover:border-gray-300 dark:border-gray-700',
                      )}
                    >
                      <p className="font-semibold text-gray-900 dark:text-white">Avancé</p>
                      <p className="mt-1 text-xs text-gray-500">Designer EduFlow complet (graphe, branches, 35+ blocs)</p>
                    </button>
                  </div>
                  <div>
                    <label htmlFor="new-formation-title" className={panelLabelClass}>
                      {t('eduflow.createFormation.titleLabel')}
                    </label>
                    <input
                      id="new-formation-title"
                      type="text"
                      value={newFormationTitle}
                      onChange={(e) => setNewFormationTitle(e.target.value)}
                      placeholder={t('eduflow.createFormation.titlePlaceholder')}
                      maxLength={200}
                      className={cn(panelInputClass, 'mt-1.5')}
                      autoFocus
                    />
                  </div>
                  <div>
                    <label htmlFor="new-formation-desc" className={panelLabelClass}>
                      {t('eduflow.createFormation.descriptionLabel')}
                    </label>
                    <textarea
                      id="new-formation-desc"
                      value={newFormationDescription}
                      onChange={(e) => setNewFormationDescription(e.target.value)}
                      placeholder={t('eduflow.createFormation.descriptionPlaceholder')}
                      rows={4}
                      maxLength={255}
                      className={cn(panelInputClass, 'mt-1.5 resize-y')}
                    />
                  </div>
                </div>
                <div className="mt-auto flex justify-end gap-3 border-t border-[color:var(--inst-player-border,#e5e7eb)] pt-4 dark:border-gray-800">
                  <Button variant="outline" onClick={() => setCreatePanelOpen(false)} disabled={creatingFlow}>
                    {t('btn.cancel')}
                  </Button>
                  <Button
                    variant="primary"
                    className={btnPrimaryInst}
                    onClick={() => void submitCreateFormation()}
                    loading={creatingFlow}
                  >
                    {t('eduflow.createFormation.validate')}
                  </Button>
                </div>
              </div>
            </div>
          </>,
          document.body,
        )}

      {/* ─── Template selection panel ─── */}
      {templatePanelOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <>
            <div
              className={panelBackdropClass}
              onClick={() => {
                if (creatingFromTemplate) return;
                setTemplatePanelOpen(false);
                setTemplateNamingMeta(null);
              }}
              aria-hidden
            />
            <div
              className={panelShellClass}
              role="dialog"
              aria-modal="true"
              aria-labelledby={templateNamingMeta ? 'template-panel-details-title' : 'template-panel-title'}
            >
              <TrainerRightPanelHeader
                icon={BookTemplate}
                title={
                  templateNamingMeta
                    ? t('eduflow.templates.detailsTitle', 'Nommer la formation')
                    : t('eduflow.templates.panelTitle', 'Créer depuis un template')
                }
                titleId={templateNamingMeta ? 'template-panel-details-title' : 'template-panel-title'}
                description={
                  templateNamingMeta
                    ? t('eduflow.templates.detailsHeaderHint', {
                        name: templateNamingMeta.sourceTitle,
                        defaultValue: `Indiquez le titre et la description. Contenu copié depuis : « ${templateNamingMeta.sourceTitle} ».`,
                      })
                    : t('eduflow.templates.panelSubtitle', 'Sélectionnez un template de formation pour démarrer rapidement.')
                }
                onClose={() => {
                  if (creatingFromTemplate) return;
                  setTemplatePanelOpen(false);
                  setTemplateNamingMeta(null);
                }}
                closeDisabled={!!creatingFromTemplate}
                closeAriaLabel={t('btn.close')}
              />
              {templateNamingMeta ? (
                <div className="border-b border-slate-200 px-6 py-2 dark:border-gray-700">
                  <button
                    type="button"
                    disabled={!!creatingFromTemplate}
                    onClick={() => setTemplateNamingMeta(null)}
                    className={cn(
                      'inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 transition hover:text-slate-900',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--inst-player-primary,#0C1C38)] focus-visible:ring-offset-2',
                      'disabled:pointer-events-none disabled:opacity-50 dark:text-gray-300 dark:hover:text-white',
                    )}
                  >
                    <ChevronLeft className={cn('h-4 w-4', rtlMirrorHorizontalChevron)} aria-hidden />
                    {t('eduflow.templates.backToTemplates', 'Retour aux templates')}
                  </button>
                </div>
              ) : null}
              <div className="flex flex-1 flex-col overflow-y-auto">
                {!templateNamingMeta ? (
                  <>
                    {/* Search */}
                    <div className="px-6 pt-4 pb-2">
                      <div className="min-w-0">
                        <InstitutionStyledSearchInput
                          value={templateSearch}
                          onChange={(e) => setTemplateSearch(e.target.value)}
                          placeholder={t('eduflow.templates.searchPlaceholder', 'Rechercher un template…')}
                        />
                      </div>
                    </div>

                    {/* List */}
                    <div className="flex-1 px-6 pb-6">
                      {loadingTemplates ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="h-6 w-6 animate-spin text-[color:var(--inst-player-primary,#0C1C38)]" />
                        </div>
                      ) : filteredTemplates.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                          <BookTemplate className="h-10 w-10 text-slate-300 dark:text-gray-600 mb-3" />
                          <p className="text-sm text-slate-500 dark:text-gray-400">
                            {templates.length === 0
                              ? t('eduflow.templates.noTemplates', 'Aucun template disponible pour le moment.')
                              : t('eduflow.templates.noMatch', 'Aucun template ne correspond à votre recherche.')}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3 pt-2">
                          {filteredTemplates.map((tpl) => (
                            <div
                              key={tpl.id}
                              className={cn(
                                'group relative flex items-start gap-4 rounded-xl border border-slate-200 bg-white p-4',
                                'hover:border-[color:var(--inst-player-primary,#0C1C38)] hover:shadow-md',
                                'transition-all duration-200 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-emerald-500',
                                creatingFromTemplate === tpl.id && 'opacity-70 pointer-events-none',
                              )}
                            >
                              {/* Cover or icon */}
                              <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg">
                                <img
                                  src={resolveFormationCoverUrl(tpl.coverImageUrl)}
                                  alt=""
                                  className="h-full w-full object-cover"
                                />
                              </div>
                              {/* Info */}
                              <div className="flex-1 min-w-0">
                                <h4
                                  className="text-sm font-semibold leading-snug text-slate-900 dark:text-white line-clamp-2 break-words"
                                  title={tpl.title}
                                >
                                  {tpl.title}
                                </h4>
                                {tpl.description && (
                                  <p className="mt-0.5 text-xs text-slate-500 dark:text-gray-400 line-clamp-2">
                                    {tpl.description}
                                  </p>
                                )}
                                <div className="mt-2 flex items-center gap-2 text-xs text-slate-400 dark:text-gray-500">
                                  {tpl._count?.blocks != null && (
                                    <span className="inline-flex items-center gap-1">
                                      <Blocks className="h-3 w-3" />
                                      {tpl._count.blocks} {t('eduflow.templates.blocks', 'blocs')}
                                    </span>
                                  )}
                                  <span>{formatRelativeDate(tpl.updatedAt)}</span>
                                </div>
                              </div>
                              {/* Action */}
                              <button
                                type="button"
                                onClick={() => openTemplateNamingStep(tpl)}
                                disabled={!!creatingFromTemplate}
                                className={cn(
                                  'flex-shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold',
                                  'bg-[color:var(--inst-player-primary,#0C1C38)] text-white',
                                  'hover:bg-[color:var(--inst-player-primary-hover,#19335E)]',
                                  'focus:outline-none focus:ring-2 focus:ring-[color:var(--inst-player-primary,#0C1C38)] focus:ring-offset-1',
                                  'transition-all duration-200 disabled:opacity-60',
                                )}
                              >
                                <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                                {t('eduflow.templates.continue', 'Continuer')}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="flex flex-1 flex-col p-6">
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="template-new-flow-title" className={panelLabelClass}>
                          {t('eduflow.createFormation.titleLabel')}
                        </label>
                        <input
                          id="template-new-flow-title"
                          type="text"
                          value={templateFormTitle}
                          onChange={(e) => setTemplateFormTitle(e.target.value)}
                          placeholder={t('eduflow.createFormation.titlePlaceholder')}
                          maxLength={200}
                          className={cn(panelInputClass, 'mt-1.5')}
                          autoFocus
                        />
                      </div>
                      <div>
                        <label htmlFor="template-new-flow-desc" className={panelLabelClass}>
                          {t('eduflow.createFormation.descriptionLabel')}
                        </label>
                        <textarea
                          id="template-new-flow-desc"
                          value={templateFormDescription}
                          onChange={(e) => setTemplateFormDescription(e.target.value)}
                          placeholder={t('eduflow.createFormation.descriptionPlaceholder')}
                          rows={4}
                          maxLength={255}
                          className={cn(panelInputClass, 'mt-1.5 resize-y')}
                        />
                      </div>
                    </div>
                    <div className="mt-auto flex justify-end gap-3 border-t border-[color:var(--inst-player-border,#e5e7eb)] pt-4 dark:border-gray-800">
                      <Button
                        variant="outline"
                        onClick={() => setTemplateNamingMeta(null)}
                        disabled={!!creatingFromTemplate}
                      >
                        {t('btn.cancel')}
                      </Button>
                      <Button
                        variant="primary"
                        className={btnPrimaryInst}
                        onClick={() => void confirmCreateFromTemplate()}
                        loading={creatingFromTemplate === templateNamingMeta.templateId}
                      >
                        {t('eduflow.templates.createAndOpenDesigner', 'Créer et ouvrir le designer')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>,
          document.body,
        )}

      {offerFlow && isLyrraOne() && (
        <PublishToStorePanel
          flowId={offerFlow.id}
          flowTitle={offerFlow.title}
          flowDescription={offerFlow.description}
          flowCoverImageUrl={offerFlow.coverImageUrl}
          flowStatus={offerFlow.status}
          onClose={() => setOfferFlow(null)}
        />
      )}
      {funnelAiFlow && isLyrraOne() && (
        <FunnelAiWizardModal
          flowId={funnelAiFlow.id}
          flowTitle={funnelAiFlow.title}
          onClose={() => setFunnelAiFlow(null)}
        />
      )}
      {categoriesFlow && (
        <FlowCategoriesPanel
          flow={categoriesFlow}
          options={categoryOptions}
          selectedIds={selectedCategoryIds}
          onToggle={toggleCategorySelection}
          onClose={() => !savingCategories && setCategoriesFlow(null)}
          onSave={saveFlowCategories}
          saving={savingCategories}
        />
      )}
      {flowSidePanel && flowSidePanel.flow.listKind !== 'institution_shell' && (
        <FlowPropertiesPanel
          flow={flowSidePanel.flow}
          mode={flowSidePanel.mode}
          onClose={() => setFlowSidePanel(null)}
          onSaved={(flowId, patch) => {
            setFlows((prev) => prev.map((fl) => (fl.id === flowId ? { ...fl, ...patch } : fl)));
          }}
          onDuplicated={(item) => {
            setFlows((prev) => [item, ...prev]);
          }}
        />
      )}
    </div>
  );
}

export default EduFlowListPage;
